/**
 * SearchOverlay Component
 * Slide-down sheet with search bar, recent searches, and trending searches.
 * Uses manual CSS transitions for smooth animation.
 */

import { ArrowLeft, Check, Clock, MapPin, Search, TrendingUp, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { formatLocation } from "@/components/shared/formatLocation"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { useUIStore } from "@/store/uiStore"
import {
  createAutocompleteService,
  fetchPlacePredictions,
  type PlaceAutocompleteService,
  type PlacePrediction,
  parsePredictionLocation,
} from "../utils/placePredictions"
import {
  clearRecentSearches,
  getRecentSearches,
  saveRecentSearch,
  TRENDING_SEARCHES,
} from "../utils/recentSearches"

interface SearchOverlayProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SearchOverlay({ open, onOpenChange }: SearchOverlayProps) {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const { t } = useTranslation("dashboard")
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState("")
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [mobileLocationFocused, setMobileLocationFocused] = useState(false)
  const [locationQuery, setLocationQuery] = useState("")
  const [locationPredictions, setLocationPredictions] = useState<PlacePrediction[]>([])
  const autocompleteServiceRef = useRef<PlaceAutocompleteService | null>(null)
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setHideMobileNav = useUIStore(s => s.setHideMobileNav)

  const location = formatLocation(user?.city, user?.state)
  const [locationDisplay, setLocationDisplay] = useState(location)

  const handleLocationSelect = useCallback(
    (loc: { city?: string; state?: string; country?: string }) => {
      setLocationDisplay(formatLocation(loc.city ?? null, loc.state ?? null))
    },
    [],
  )

  const handleLocationClear = useCallback(() => {
    setLocationDisplay("")
  }, [])

  useEffect(() => {
    createAutocompleteService(autocompleteServiceRef)
  }, [])

  useEffect(() => {
    return () => {
      if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current)
    }
  }, [])

  const handleMobileLocationClose = useCallback(() => {
    setMobileLocationFocused(false)
    setLocationQuery("")
    setLocationPredictions([])
  }, [])

  const handleLocationSearchInput = useCallback((value: string) => {
    setLocationQuery(value)
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current)
    if (!value.trim()) {
      setLocationPredictions([])
      return
    }
    locationDebounceRef.current = setTimeout(() => {
      fetchPlacePredictions(autocompleteServiceRef.current, value, setLocationPredictions)
    }, 200)
  }, [])

  const handlePredictionSelect = useCallback(
    (prediction: PlacePrediction) => {
      handleLocationSelect(parsePredictionLocation(prediction))
      setMobileLocationFocused(false)
      setLocationQuery("")
      setLocationPredictions([])
    },
    [handleLocationSelect],
  )

  useEffect(() => {
    setHideMobileNav(mobileLocationFocused)
    return () => setHideMobileNav(false)
  }, [mobileLocationFocused, setHideMobileNav])

  const hasLocation = !!locationDisplay

  // Handle open/close with animation
  useEffect(() => {
    if (open && !isVisible) {
      // Opening: show first with slide-down
      setIsVisible(true)
      setIsExiting(false)
      // Small delay to ensure CSS transition picks up the change
      setTimeout(() => setIsAnimating(true), 10)
      // Focus after animation starts
      setTimeout(() => inputRef.current?.focus(), 350)
    } else if (!open && isVisible) {
      // Closing: slide up, then hide
      setIsAnimating(false)
      setIsExiting(true)
      setTimeout(() => {
        setIsVisible(false)
        setIsExiting(false)
      }, 500) // Match transition duration
    }
  }, [open, isVisible])

  // Load recent searches when overlay opens
  useEffect(() => {
    if (open) {
      setRecentSearches(getRecentSearches())
      setQuery("")
    }
  }, [open])

  const handleSubmit = useCallback(
    (searchQuery: string) => {
      const trimmed = searchQuery.trim()
      if (trimmed.length < 2) return
      saveRecentSearch(trimmed)
      onOpenChange(false)
      navigate(`/search?q=${encodeURIComponent(trimmed)}`)
    },
    [navigate, onOpenChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSubmit(query)
      }
    },
    [query, handleSubmit],
  )

  const handleClearHistory = useCallback(() => {
    clearRecentSearches()
    setRecentSearches([])
  }, [])

  // Don't render if not visible (but allow exit animation to complete)
  if (!isVisible && !isExiting) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[rgb(0,0,0,1)] backdrop-blur-sm"
        style={{
          opacity: isExiting ? 0 : isAnimating ? 1 : 0,
          transition: "opacity 0.3s ease-out",
        }}
        onClick={() => onOpenChange(false)}
      />
      {/* Sheet Content */}
      <div
        className={`absolute left-0 right-0 top-0 bg-my-rituality-bg ${mobileLocationFocused ? "" : "border-b border-border"} origin-top`}
        style={{
          transform: isExiting
            ? "translateY(-100%)"
            : isAnimating
              ? "translateY(0)"
              : "translateY(-100%)",
          transition: "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Accessible title */}
        <h2 className="sr-only">{t("search.searchAria")}</h2>

        <div className="max-w-[1400px] mx-auto px-4 md:px-[50px] md:py-5.5 my-2.5">
          {/* Top row: close button */}
          <div className="flex justify-between md:justify-end mb-5">
            {/* Desktop: X button */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="hidden md:flex size-[40px] items-center justify-center text-text-primary hover:text-text-primary/70 transition-colors cursor-pointer border border-white/25 rounded-full p-2"
              aria-label={t("search.closeSearch")}
            >
              <X className="size-[20px]" />
            </button>
            {/* Mobile: back arrow */}
            {!mobileLocationFocused ? (
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="md:hidden flex size-[30px] items-center justify-center text-text-primary hover:text-text-primary/70 transition-colors"
                aria-label={t("search.closeSearch")}
              >
                <ArrowLeft className="size-[25px]" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleMobileLocationClose}
                className="flex items-end w-full justify-end text-text-primary opacity-70 hover:opacity-100"
              >
                <X className="w-[24px] h-[24px]" />
              </button>
            )}
          </div>

          {/* Search bar */}
          <div className="flex items-center bg-connect-glass-bg border border-connect-glass-border rounded-[15px] h-[65px] px-[19px]">
            {/* Normal search input - hidden on mobile when location focused */}
            <div
              className={`flex items-center flex-1 min-w-0 ${mobileLocationFocused ? "hidden md:flex" : "flex"}`}
            >
              <Search className="w-[23px] h-[23px] text-text-primary opacity-90 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder={t("search.searchPagePlaceholder")}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent border-none outline-none text-[14px] font-semibold text-text-primary opacity-90 placeholder:text-text-primary placeholder:opacity-90 placeholder:font-normal px-2"
                aria-label={t("search.searchAria")}
              />
            </div>
            {/* Mobile location search input - replaces search bar on mobile */}
            {mobileLocationFocused && (
              <div className="flex md:hidden items-center flex-1 min-w-0 relative">
                <MapPin className="w-[22px] h-[22px] text-text-primary opacity-90 flex-shrink-0" />
                <input
                  type="text"
                  value={locationQuery}
                  onChange={e => handleLocationSearchInput(e.target.value)}
                  placeholder={t("search.locationPlaceholder")}
                  className="flex-1 bg-transparent border-none outline-none text-[14px] font-semibold text-text-primary opacity-90 placeholder:text-text-primary placeholder:opacity-50 placeholder:font-normal px-2 pr-8"
                  autoFocus
                />
              </div>
            )}
            {/* Desktop location area */}
            {location && (
              <div className="hidden md:flex items-center gap-1.5 flex-shrink-0 max-w-[200px]">
                <MapPin className="w-[25px] h-[25px] text-text-primary opacity-90 shrink-0" />
                {hasLocation ? (
                  <>
                    <AutocompleteInput
                      label=""
                      mode="full"
                      placeholder=""
                      value={locationDisplay}
                      onLocationSelect={handleLocationSelect}
                      className="h-auto min-h-0 p-0 border-0 text-[14px] font-semibold text-text-primary opacity-90 bg-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleLocationClear}
                      className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/20 transition-colors"
                      aria-label={t("search.clearLocation")}
                    >
                      <X className="w-3 h-3 text-text-primary opacity-70" />
                    </button>
                  </>
                ) : (
                  <AutocompleteInput
                    label=""
                    mode="full"
                    placeholder={t("search.anyLocation")}
                    value=""
                    onLocationSelect={handleLocationSelect}
                    className="h-auto min-h-0 p-0 border-0 text-[14px] font-semibold text-text-primary opacity-90 bg-transparent"
                  />
                )}
              </div>
            )}
          </div>

          {/* Location trigger pill - mobile only */}
          {location && !mobileLocationFocused && (
            <button
              type="button"
              onClick={() => setMobileLocationFocused(true)}
              className="flex md:hidden items-center gap-1.25 mt-3.75 text-[14px] font-semibold text-text-primary whitespace-nowrap opacity-90 rounded-[10px] border border-brand-offwhite/10 px-[10px]"
            >
              <MapPin className="w-4 h-4 shrink-0" />
              <span>{hasLocation ? locationDisplay : t("search.anyLocation")}</span>
            </button>
          )}

          {/* Mobile Location Predictions */}
          {mobileLocationFocused && (
            <div className="flex md:hidden">
              {locationPredictions.length > 0 ? (
                <div className="w-full mt-5">
                  {locationPredictions.map(prediction => {
                    const secondary = prediction.structured_formatting?.secondary_text ?? ""
                    const statePart = secondary.split(",")[0]?.trim() ?? ""
                    const mainText =
                      prediction.structured_formatting?.main_text ?? prediction.description
                    const predictionDisplay = statePart ? `${mainText}, ${statePart}` : mainText
                    const isSelected = locationDisplay === predictionDisplay
                    return (
                      <button
                        key={prediction.place_id}
                        type="button"
                        onClick={() => handlePredictionSelect(prediction)}
                        className="flex items-center gap-2.5 w-full py-3.75 text-left border-b border-[#F2F0EB1A] cursor-pointer"
                      >
                        <MapPin className="w-5 h-5 shrink-0 text-primary opacity-90" />
                        <span className="text-[14px] font-semibold text-primary flex-1">
                          {predictionDisplay}
                        </span>
                        {isSelected && <Check className="w-5 h-5 shrink-0 text-brand-olive" />}
                      </button>
                    )
                  })}
                </div>
              ) : (
                locationQuery.trim().length > 0 && (
                  <div className="flex items-center justify-center py-10">
                    <p className="text-[14px] text-text-primary/50">
                      {t("search.noLocationsFound")}
                    </p>
                  </div>
                )
              )}
            </div>
          )}

          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div
              className={`mt-2.5 bg-my-rituality-bg md:border border-white/10 rounded-[20px] py-8.5 md:py-5 md:p-5 ${mobileLocationFocused ? "hidden md:block" : ""}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <Clock className="w-[25px] h-[25px] text-brand-olive" />
                  <span className="text-[14px] font-semibold text-brand-olive">
                    {t("search.recentSearches")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleClearHistory}
                  className="text-[14px] font-semibold text-text-primary hover:text-text-primary/70 transition-colors cursor-pointer"
                >
                  {t("search.clearHistory")}
                </button>
              </div>
              <div className="flex flex-col gap-3.5">
                {recentSearches.map(search => (
                  <button
                    key={search}
                    type="button"
                    onClick={() => handleSubmit(search)}
                    className="text-left text-[14px] leading-[25px] font-semibold text-text-primary/90 hover:text-text-primary transition-colors cursor-pointer"
                  >
                    {search}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Trending Searches */}
          <div
            className={`mt-3 bg-my-rituality-bg md:border border-white/10 rounded-[20px] md:p-5 py-6 md:py-5 ${mobileLocationFocused ? "hidden md:block" : ""}`}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <TrendingUp className="w-[23px] h-[23px] text-brand-olive" />
              <span className="text-[14px] font-semibold text-white opacity-50">
                {t("search.trendingSearches")}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-5">
              {TRENDING_SEARCHES.map(search => (
                <button
                  key={search}
                  type="button"
                  onClick={() => handleSubmit(search)}
                  className="bg-brand-olive/5 border border-brand-olive rounded-md px-2.5 py-2.5 text-[13px] text-brand-olive hover:bg-brand-olive/10 transition-colors leading-[20px] cursor-pointer"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* CSS Keyframes injection */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `}</style>
    </div>
  )
}
